import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { AlertTriangle, User, Mail, Phone, Building2, CheckCircle2 } from 'lucide-react'
import type { DuplicateClientDto } from '@/lib/api/v2/projects'

interface DuplicateClientsModalProps {
  open: boolean
  duplicates: DuplicateClientDto[]
  onCancel: () => void
  onCreateAnyway: () => void
  /** Reuse a proposed client instead of creating a new one. */
  onUseExisting?: (customerId: string, name: string) => void
  isCreating?: boolean
}

const REASON_LABELS: Record<string, string> = {
  email_exact: 'Email identique',
  phone_exact: 'Téléphone identique',
  contact_lastname_exact: 'Nom de contact identique',
  name_similar: 'Nom similaire',
  vat_exact: 'Numéro de TVA identique',
  customer_name_exact: 'Nom identique',
  registry_exact: "Numéro d'entreprise identique",
  gln_exact: 'GLN identique',
}

const CIVILITY_LABELS: Record<number, string> = {
  1: 'M.',
  2: 'Mme',
  3: 'M. et Mme',
}

const CLIENT_TYPE_LABELS: Record<number, string> = {
  1: 'Particulier',
  2: 'Société',
}

function getScoreColor(score: number): string {
  if (score >= 80) return 'bg-red-100 text-red-800 border-red-200'
  if (score >= 50) return 'bg-amber-100 text-amber-800 border-amber-200'
  return 'bg-slate-100 text-slate-700 border-slate-200'
}

function getScoreLabel(score: number): string {
  if (score >= 80) return 'Très similaire'
  if (score >= 50) return 'Similaire'
  return 'Possiblement similaire'
}

export function DuplicateClientsModal({
  open,
  duplicates,
  onCancel,
  onCreateAnyway,
  onUseExisting,
  isCreating = false,
}: DuplicateClientsModalProps) {
  return (
    <Dialog open={open} onOpenChange={(open) => !open && !isCreating && onCancel()}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-full">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <DialogTitle>Clients similaires détectés</DialogTitle>
              <DialogDescription>
                {duplicates.length} client{duplicates.length > 1 ? 's' : ''} avec des informations similaires {duplicates.length > 1 ? 'ont été trouvés' : 'a été trouvé'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* Warning message */}
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              💡 Vérifiez si l'un de ces clients correspond à celui que vous essayez de créer pour éviter les doublons.
            </p>
          </div>

          {/* Duplicate clients list */}
          <div className="space-y-3">
            {duplicates.map((duplicate) => (
              <div key={duplicate.customerId} className="border rounded-lg p-4 space-y-3">
                {/* Header with score */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <h3 className="font-semibold">{duplicate.name || 'Sans nom'}</h3>
                      <Badge variant="outline" className="text-xs">
                        {CLIENT_TYPE_LABELS[duplicate.type] || 'Type inconnu'}
                      </Badge>
                    </div>
                    {duplicate.vatNumber && (
                      <p className="text-sm text-muted-foreground">TVA: {duplicate.vatNumber}</p>
                    )}
                    {duplicate.hasActiveProject === false && (
                      <p className="text-sm text-muted-foreground" data-testid="duplicate-no-active-project">
                        Aucun projet actif : ce client n'apparaît plus dans la recherche.
                      </p>
                    )}
                  </div>
                  <Badge className={`${getScoreColor(duplicate.score)} border`}>
                    {duplicate.score}% - {getScoreLabel(duplicate.score)}
                  </Badge>
                </div>

                {/* Match reasons */}
                <div className="flex flex-wrap gap-2">
                  {duplicate.reasons.map((reason) => (
                    <Badge key={reason} variant="secondary" className="text-xs">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      {REASON_LABELS[reason] || reason}
                    </Badge>
                  ))}
                </div>

                {/* Contacts */}
                {duplicate.contacts.length > 0 && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase">Contacts</p>
                      {duplicate.contacts.map((contact) => (
                        <div key={contact.id} className="flex items-start gap-3 text-sm">
                          <User className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <div className="flex-1 space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">
                                {contact.civility ? CIVILITY_LABELS[contact.civility] : ''}{' '}
                                {contact.firstName} {contact.lastName}
                              </span>
                              {contact.isPrimary && (
                                <Badge variant="outline" className="text-xs">
                                  Principal
                                </Badge>
                              )}
                            </div>
                            {contact.email && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Mail className="h-3 w-3" />
                                <span className="text-xs">{contact.email}</span>
                              </div>
                            )}
                            {contact.phone && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Phone className="h-3 w-3" />
                                <span className="text-xs">{contact.phone}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {onUseExisting && (
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={isCreating}
                      data-testid="duplicate-use-existing"
                      onClick={() => onUseExisting(duplicate.customerId, duplicate.name || 'Client')}
                    >
                      Utiliser ce client
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isCreating}
            className="w-full sm:w-auto"
          >
            Annuler
          </Button>
          <Button
            type="button"
            onClick={onCreateAnyway}
            disabled={isCreating}
            className="w-full sm:w-auto"
          >
            {isCreating ? 'Création...' : 'Créer quand même'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
